import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { SelectItem } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { FormField } from '../../components/shared/FormField';
import { SelectField } from '../../components/shared/SelectField';
import { ResponsiveDataView } from '../../components/shared/ResponsiveDataView';
import { StatusToggle } from '../../components/shared/StatusToggle';
import UserSelect from '../../components/shared/UserSelect';
import { User } from '../../services/userService';
import ActionDropdown, { ActionItem } from '../../components/Common/ActionDropdown';
import ConfirmModal from '../../components/Common/ConfirmModal';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { useCurrency } from '../../context/CurrencyContext';
import { formatCount } from '../../lib/formatNumber';
import cloudAssetService, {
  CloudEnvironment,
  CloudTier,
} from '../../services/cloudAssetService';
import { useCloudProviders } from './useCloudProviders';


interface Props {
  environments: CloudEnvironment[];
  /** Environment tier master (User Story 95) — feeds the Tier dropdown. */
  tiers: CloudTier[];
  /** Full user records — `UserSelect` searches and displays email too. */
  users: User[];
  loading: boolean;
  onChanged: () => void;
  /**
   * Which context this tab is mounted in.
   *
   * `register` (Cloud Assets) shows Owner, Resources and Monthly Cost — Cloud
   * Ops read the environment alongside what is actually in it.
   * `master` (Cloud Setup) drops those three: Owner is a property of the record
   * rather than something you administer from a list, and the two rollups are
   * facts about the register, not about the master row. Editing an environment
   * still exposes Owner in the dialog either way.
   */
  variant?: 'register' | 'master';
}

/** Radix Select has no concept of an empty option value, so an explicitly
 * cleared optional select round-trips through this sentinel. */
const NONE = '__none__';

const EMPTY = {
  provider: '',
  name: '',
  providerAccountId: '',
  environmentTier: '',
  ownerId: '',
  isActive: true,
  notes: '',
};

/**
 * Cloud environments master (User Stories 81, 93).
 *
 * Resource count and monthly cost are computed server-side and scoped — an
 * environment row shows only the portion of itself the viewer is allowed to open.
 * There is no last-sync column: nothing syncs.
 */
export const CloudEnvironmentsTab: React.FC<Props> = ({
  environments,
  tiers,
  users,
  loading,
  onChanged,
  variant = 'register',
}) => {
  const showRollups = variant === 'register';
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const { formatCost } = useCurrency();
  const { providers, providerLabel } = useCloudProviders();
  const canManage = hasPermission('cloud.manage');

  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CloudEnvironment | null>(null);
  const [form, setForm] = useState<Record<string, any>>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<CloudEnvironment | null>(null);
  const [pendingToggle, setPendingToggle] = useState<CloudEnvironment | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    if (!dialogOpen) return;
    setErrors({});
    setForm(
      editing
        ? {
            provider: editing.provider,
            name: editing.name,
            providerAccountId: editing.providerAccountId,
            environmentTier: editing.environmentTier ?? '',
            ownerId: editing.ownerId ?? '',
            isActive: editing.isActive,
            notes: editing.notes ?? '',
          }
        : // Providers are master data now, so a new environment starts on the first
          // one configured rather than an assumed "aws".
          { ...EMPTY, provider: providers[0]?.code ?? '' },
    );
  }, [dialogOpen, editing, providers]);

  /**
   * Active tiers plus the one being edited, so an environment on a retired tier
   * shows what it actually carries instead of rendering blank (User Story 95).
   */
  const selectableTiers = useMemo(() => {
    const currentValue = editing?.environmentTier ?? null;
    return tiers.filter(
      (tier) => tier.isActive !== false || tier.value === currentValue,
    );
  }, [tiers, editing]);

  // The environment list is small and already fully loaded, so filtering here
  // avoids a round trip the endpoint does not offer anyway.
  const visibleEnvironments = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return environments;
    return environments.filter((environment) =>
      [
        environment.name,
        environment.providerAccountId,
        environment.environmentTier,
        providerLabel(environment.provider),
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(term)),
    );
  }, [environments, search, providerLabel]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (environment: CloudEnvironment) => {
    setEditing(environment);
    setDialogOpen(true);
  };

  const set = (key: string, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!String(form.provider).trim()) next.provider = 'Provider is required';
    if (!String(form.name).trim()) next.name = 'Name is required';
    if (!String(form.providerAccountId).trim()) {
      next.providerAccountId = 'Provider Account ID is required';
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    const payload: Record<string, any> = { ...form };
    // On edit an emptied owner must be sent as null, not dropped, or clearing
    // the owner silently keeps the old one. On create there is nothing to
    // clear, so omitting it leaves the column NULL as intended.
    if (payload.ownerId === '') {
      if (editing) payload.ownerId = null;
      else delete payload.ownerId;
    }
    if (payload.environment === '') delete payload.environment;
    if (payload.notes === '') delete payload.notes;

    setSaving(true);
    try {
      if (editing) {
        await cloudAssetService.updateEnvironment(editing.id, payload);
        showToast('Cloud environment updated', 'success');
      } else {
        await cloudAssetService.createEnvironment(payload);
        showToast('Cloud environment created', 'success');
      }
      setDialogOpen(false);
      onChanged();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to save environment', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const environment = pendingDelete;
    setPendingDelete(null);
    try {
      await cloudAssetService.deleteEnvironment(environment.id);
      showToast('Cloud environment deleted', 'success');
      onChanged();
    } catch (error: any) {
      // The server refuses while resources still reference the environment; that
      // message is the useful one, so surface it verbatim.
      showToast(error?.friendlyMessage || 'Failed to delete environment', 'error');
    }
  };

  /**
   * Retiring an environment is the reversible half of this master (User Story
   * 94): it drops out of the Cloud Resource dropdown immediately, while the
   * resources already in it keep it. `onChanged` refetches the page-level
   * environment list, which is the same array the resource form reads, so the
   * dropdown reflects the change without a reload.
   */
  const confirmToggle = async () => {
    if (!pendingToggle) return;
    const environment = pendingToggle;
    const next = !environment.isActive;
    setPendingToggle(null);
    setTogglingId(environment.id);
    try {
      await cloudAssetService.updateEnvironment(environment.id, { isActive: next });
      showToast(
        `${environment.name} ${next ? 'activated' : 'deactivated'}`,
        'success',
      );
      onChanged();
    } catch (error: any) {
      showToast(
        error?.friendlyMessage || 'Failed to update environment status',
        'error',
      );
    } finally {
      setTogglingId(null);
    }
  };

  const statusCell = (environment: CloudEnvironment) => (
    <StatusToggle
      checked={environment.isActive}
      onToggle={canManage ? () => setPendingToggle(environment) : undefined}
      loading={togglingId === environment.id}
      ariaLabel={`Toggle status for ${environment.name}`}
    />
  );

  const rowActions = (environment: CloudEnvironment): ActionItem[] => [
    {
      label: 'Edit',
      icon: <Pencil className="h-4 w-4" />,
      onClick: () => openEdit(environment),
      variant: 'default',
    },
    {
      label: 'Delete',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: () => setPendingDelete(environment),
      variant: 'danger',
    },
  ];

  const ownerName = (environment: CloudEnvironment) =>
    environment.owner ? `${environment.owner.firstName} ${environment.owner.lastName}` : '—';

  const emptyState = (
    <div className="py-10 text-center text-sm text-muted-foreground">
      {loading
        ? 'Loading environments…'
        : search.trim()
          ? 'No cloud environments match your search.'
          : 'No cloud environments yet. Add one before importing resources.'}
    </div>
  );

  return (
    <div>
      <div className="flex flex-col gap-2 border-b p-4 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="relative min-w-0 md:w-96">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            className="pl-9"
            placeholder="Search environments..."
            aria-label="Search environments"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {canManage && (
          <Button size="sm" onClick={openCreate} className="md:shrink-0">
            <Plus className="mr-2 h-4 w-4" />
            Add Environment
          </Button>
        )}
      </div>

      <p className="border-b px-4 py-2 text-xs text-muted-foreground sm:px-6">
        {showRollups
          ? 'Environments — the environments, subscriptions and projects resources are billed against. Resource counts and spend are calculated from the register.'
          : 'Environments — the environments, subscriptions and projects resources are billed against. Resource counts and spend are shown on the Cloud Assets page.'}
      </p>

      <ResponsiveDataView
        items={visibleEnvironments}
        label="Cloud environments"
        empty={emptyState}
        toCard={(environment) => ({
          key: environment.id,
          eyebrow: statusCell(environment),
          title: environment.name,
          subtitle: `${providerLabel(environment.provider)} · ${environment.providerAccountId}`,
          fields: [
            { label: 'Environment Tier', value: environment.environmentTier || '—' },
            ...(showRollups
              ? [
                  { label: 'Owner', value: ownerName(environment) },
                  {
                    label: 'Resources',
                    value: formatCount(environment.resourceCount ?? 0),
                  },
                  {
                    label: 'Monthly Cost',
                    value: formatCost(environment.monthlyCost ?? 0),
                  },
                ]
              : []),
          ],
          actions: canManage ? <ActionDropdown actions={rowActions(environment)} /> : undefined,
        })}
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Provider</TableHead>
              <TableHead>Environment</TableHead>
              <TableHead>Provider Account ID</TableHead>
              <TableHead>Tier</TableHead>
              {showRollups && (
                <>
                  <TableHead>Owner</TableHead>
                  <TableHead className="text-right">Resources</TableHead>
                  <TableHead className="text-right">Monthly Cost</TableHead>
                </>
              )}
              <TableHead>Status</TableHead>
              {canManage && <TableHead className="w-[50px]" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleEnvironments.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={(showRollups ? 8 : 5) + (canManage ? 1 : 0)}
                  className="py-8"
                >
                  {emptyState}
                </TableCell>
              </TableRow>
            ) : (
              visibleEnvironments.map((environment) => (
                <TableRow key={environment.id}>
                  <TableCell>{providerLabel(environment.provider)}</TableCell>
                  <TableCell className="font-semibold text-primary">
                    {environment.name}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {environment.providerAccountId}
                  </TableCell>
                  <TableCell>{environment.environmentTier || '—'}</TableCell>
                  {showRollups && (
                    <>
                      <TableCell>{ownerName(environment)}</TableCell>
                      <TableCell className="text-right">
                        {formatCount(environment.resourceCount ?? 0)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCost(environment.monthlyCost ?? 0)}
                      </TableCell>
                    </>
                  )}
                  <TableCell>{statusCell(environment)}</TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <ActionDropdown actions={rowActions(environment)} />
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ResponsiveDataView>

      <Dialog open={dialogOpen} onOpenChange={(next) => !next && setDialogOpen(false)}>
        <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-lg sm:p-0">
          <DialogHeader className="shrink-0 border-b px-6 py-4">
            <DialogTitle>
              {editing ? 'Edit Cloud Environment' : 'Add Cloud Environment'}
            </DialogTitle>
            <DialogDescription>
              An account, subscription or project that cloud resources are billed
              against.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            <FormField
              label="Provider"
              required
              error={errors.provider}
              id="environment-provider"
            >
              <SelectField
                value={form.provider}
                onValueChange={(value) => set('provider', value)}
                placeholder="Select provider"
              >
                {providers.map((option) => (
                  <SelectItem key={option.code} value={option.code}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectField>
            </FormField>

            <FormField label="Name" required error={errors.name} id="environment-name">
              <Input
                id="environment-name"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Production AWS"
              />
            </FormField>

            <FormField
              label="Provider Account ID"
              required
              error={errors.providerAccountId}
              id="environment-identifier"
            >
              <Input
                id="environment-identifier"
                value={form.providerAccountId}
                onChange={(e) => set('providerAccountId', e.target.value)}
                placeholder="123456789012"
              />
            </FormField>

            <FormField label="Environment Tier" id="environment-tier">
              <SelectField
                value={form.environmentTier || ''}
                onValueChange={(value) =>
                  set('environmentTier', value === NONE ? '' : value)
                }
                placeholder="No tier"
              >
                <SelectItem value={NONE}>No tier</SelectItem>
                {selectableTiers.map((tier) => (
                  <SelectItem key={tier.id} value={tier.value}>
                    {tier.label}
                    {tier.isActive === false ? ' (Inactive)' : ''}
                  </SelectItem>
                ))}
              </SelectField>
            </FormField>

            <FormField label="Owner" id="environment-owner">
              <UserSelect
                id="environment-owner"
                users={users}
                value={form.ownerId}
                onChange={(userId) => set('ownerId', userId ?? '')}
                placeholder="Select owner..."
                clearable
              />
            </FormField>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label htmlFor="environment-active">Active</Label>
                <p className="text-xs text-muted-foreground">
                  Only active environments can be picked on a cloud resource.
                  Resources already in this one keep it either way.
                </p>
              </div>
              <Switch
                id="environment-active"
                checked={Boolean(form.isActive)}
                onCheckedChange={(checked) => set('isActive', checked)}
              />
            </div>
            </div>

            <DialogFooter className="mx-0 mt-0 shrink-0 gap-2 border-t bg-background px-6 py-3 sm:gap-0 sm:border-t sm:bg-background sm:px-6 sm:py-3">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="min-w-[140px]">
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Environment'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        show={Boolean(pendingToggle)}
        title={
          pendingToggle?.isActive
            ? 'Deactivate Cloud Environment'
            : 'Activate Cloud Environment'
        }
        message={
          pendingToggle?.isActive
            ? `Deactivate "${pendingToggle?.name}"? It will no longer be selectable on a cloud resource.${
                pendingToggle?.resourceCount
                  ? ` The ${formatCount(pendingToggle.resourceCount)} resource(s) already in it keep it and are not changed.`
                  : ''
              }`
            : `Activate "${pendingToggle?.name ?? ''}"? It becomes selectable on cloud resources again.`
        }
        confirmText={pendingToggle?.isActive ? 'Deactivate' : 'Activate'}
        type={pendingToggle?.isActive ? 'warning' : 'primary'}
        onConfirm={confirmToggle}
        onCancel={() => setPendingToggle(null)}
      />

      <ConfirmModal
        show={Boolean(pendingDelete)}
        title="Delete Cloud Environment"
        message={`Delete "${pendingDelete?.name ?? ''}"? This cannot be undone.`}
        confirmText="Delete"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default CloudEnvironmentsTab;
